import { supabase } from "@/integrations/supabase/client";
import {
  callerE164FromCallLog,
  e164MatchVariants,
  isMemoryRowExpired,
  isSameCaller,
  normaliseE164,
  parseMobileContactPacket,
} from "@/lib/contact/mobileContactPacket";
import {
  MOBILE_CONTACT_PACKET_MEMORY_KEY,
  MOBILE_CONTACT_PACKET_SCOPE,
  type ConversationContactPacket,
  type MobileContactPacket,
} from "@/types/contact/mobileContactPacket";
import type { Conversation } from "@/types/conversation/conversation";

/**
 * Reads the contact packet for a conversation's caller.
 *
 * The pipe is: conversation -> caller number -> caller-scoped brain memory.
 * Both hops are reads only; nothing here writes. The packet's producer is
 * Help Genie Mobile (HG-1299), which is not built yet, so a null packet is
 * the normal answer today and is returned as null rather than as a filled-in
 * placeholder.
 *
 * Goes straight to the data client rather than through `ApiService` because
 * neither `call_logs` nor `account_memory` has an `api` resource, and both are
 * owner-scoped by row-level security — the same pattern
 * `src/components/phone/CallLogs.tsx` already uses for call logs.
 */
export class MobileContactPacketService {
  /**
   * The caller's number for a conversation, in E.164.
   *
   * Prefers the call metadata carried on the conversation itself, then falls
   * back to the call log written when the call was recorded. Returns null for
   * anything without a phone leg (web chat, meeting recordings) — there is no
   * caller to match on, and that is a real answer, not a failure.
   */
  static async getCallerE164(conversation: Conversation | null): Promise<string | null> {
    const fromConversation = normaliseE164(
      conversation?.metadata?.phone_call?.external_number,
    );
    if (fromConversation) return fromConversation;

    const elevenlabsId = conversation?.elevenlabs_id;
    if (!elevenlabsId) return null;

    const columns = "from_number, to_number, metadata";

    const { data: byConversationId } = await supabase
      .from("call_logs")
      .select(columns)
      .eq("metadata->>elevenlabs_conversation_id", elevenlabsId)
      .limit(1)
      .maybeSingle();

    if (byConversationId) return callerE164FromCallLog(byConversationId);

    // `saveCallLog` falls back to the conversation id when the carrier didn't
    // give us a call SID, so the same id can land in `call_sid` instead.
    const { data: byCallSid } = await supabase
      .from("call_logs")
      .select(columns)
      .eq("call_sid", elevenlabsId)
      .limit(1)
      .maybeSingle();

    return byCallSid ? callerE164FromCallLog(byCallSid) : null;
  }

  /**
   * The contact packet a caller has shared, or null when they haven't shared
   * anything. Matches on `caller_e164` against the existing brain memory
   * store — no separate contact table.
   */
  static async getPacketForCaller(
    callerE164: string | null | undefined,
  ): Promise<MobileContactPacket | null> {
    const normalised = normaliseE164(callerE164);
    if (!normalised) return null;

    const variants = e164MatchVariants(normalised);
    if (!variants.length) return null;

    const { data, error } = await supabase
      .from("account_memory")
      .select("caller_e164, metadata, created_at, updated_at, expires_at")
      .eq("scope", MOBILE_CONTACT_PACKET_SCOPE)
      .eq("memory_key", MOBILE_CONTACT_PACKET_MEMORY_KEY)
      .in("caller_e164", variants)
      .order("updated_at", { ascending: false })
      .limit(5);

    if (error) throw error;

    // Re-verify the caller after the query: the filter widens on stored
    // formatting, the match must not widen on who the packet belongs to.
    // Expired rows are dropped — a lapsed packet is not current detail.
    const row = (data ?? []).find(
      (candidate) =>
        isSameCaller(candidate.caller_e164, normalised) && !isMemoryRowExpired(candidate),
    );

    return row ? parseMobileContactPacket(row) : null;
  }

  /** Resolve a conversation all the way to its caller's packet. */
  static async getForConversation(
    conversation: Conversation | null,
  ): Promise<ConversationContactPacket> {
    const callerE164 = await this.getCallerE164(conversation);
    if (!callerE164) return { callerE164: null, packet: null };

    return { callerE164, packet: await this.getPacketForCaller(callerE164) };
  }
}
